/**
 * Appointment Client-to-Display Adapter
 * 
 * Transforms ClientAppointment into FullCalendar EventInput objects with strongly typed extendedProps
 */

import { ClientAppointment, CalendarEvent, AppointmentTransformOptions, ClientContinuousAppointment, ClientTherapy, isIntervalPriced } from "../types";
import { mobiliTheme } from "../../../../../themes/mobiliTheme";
import moment from "moment";

/** A slot as far as grouping is concerned: when it runs, and who runs it. */
interface GroupableSlot {
  id: string;
  start: Date;
  end: Date;
  user?: { id?: string } | null;
}

/**
 * FullCalendar drags every event sharing a `groupId` as one block, so the
 * groupId is what decides which of an appointment's time slots move together.
 *
 * Slots move together when they are **back to back with each other on the same
 * therapist** — the case where the times are not independently meaningful:
 * they are one continuous stretch of treatment, and moving half of it would
 * open a hole. Slots separated by a gap, or handed to different therapists,
 * are separate arrangements and move on their own.
 *
 * "Nicht gruppieren" (`groupCheck === false`) turns grouping off entirely, for
 * when a back-to-back pair does need to be split up.
 *
 * Returns slotId → groupId, with entries only for slots in a run of two or
 * more. A slot with no entry gets no groupId and drags alone. The run index is
 * part of the id so that two separate runs within one appointment (e.g. morning
 * pair + afternoon pair) stay separate groups.
 */
export const buildContiguousRunIds = (
  appointmentId: string,
  timeSlots: GroupableSlot[],
  groupCheck: boolean
): Map<string, string> => {
  const runIds = new Map<string, string>();
  if (!groupCheck || timeSlots.length < 2) return runIds;

  const ordered = [...timeSlots].sort((a, b) => a.start.getTime() - b.start.getTime());
  let run: GroupableSlot[] = [];
  let runIndex = 0;

  const flush = () => {
    if (run.length > 1) {
      const groupId = `${appointmentId}-${run[0].user?.id ?? "none"}-${runIndex}`;
      run.forEach(slot => runIds.set(slot.id, groupId));
      runIndex += 1;
    }
    run = [];
  };

  for (const slot of ordered) {
    const previous = run[run.length - 1];
    const continues =
      previous &&
      previous.end.getTime() === slot.start.getTime() &&
      previous.user?.id === slot.user?.id;
    if (!continues) flush();
    run.push(slot);
  }
  flush();

  return runIds;
};

/**
 * Transform ClientAppointment to FullCalendar EventInput
 */
export const adaptAppointmentToCalendarEvent = (
  appointment: ClientAppointment,
  options: AppointmentTransformOptions = {}
): CalendarEvent[] => {
  const { groupCheck = false, lockedAppointments = [], sublocalities } = options;
  const runIds = buildContiguousRunIds(appointment.id, appointment.timeSlots, groupCheck);

  return appointment.timeSlots.map(timeSlot => {
    const groupTimeSlotTherapist= timeSlot.type==="GroupTimeSlotTherapist";
    // A participant slot carries a groupAppointmentId only while it belongs to a
    // group; cancelling (or rescheduling out of) the group clears it.
    const isGroupTimeSlotPatient = timeSlot.type === "GroupTimeSlotPatient" && !!timeSlot.groupAppointmentId;
    const groupId = isGroupTimeSlotPatient
      ?  `${timeSlot.groupAppointmentId}-${timeSlot.user?.id}`
      : (groupCheck ? `${appointment.id}-${timeSlot.user?.id}` : "");
    const isLocked = lockedAppointments.includes(appointment.id);    
    const sublocality = sublocalities?.find(subloc => subloc?.addressId === appointment.address?.id);
    const allParticipantsAttended = groupTimeSlotTherapist
      ? (appointment.participants?.length
          ? appointment.participants.every(participant =>
              participant.timeSlots?.some(ts => ts.attended)
            )
          : false)
      : false;

    if (!timeSlot.therapy && !groupTimeSlotTherapist) {
      // GroupTimeSlotTherapist legitimately has no therapy; only warn otherwise.
      console.warn("Therapy is undefined for time slot.", timeSlot);
    }
    return {
      id: timeSlot.id,
      title: appointment.displayTitle,
      start: timeSlot.start.toISOString(),
      end: timeSlot.end.toISOString(),
      resourceId: timeSlot.user?.id,
      groupId,
      editable: !isLocked,
      // MD-328: a treatment's duration is normally fixed — it is the
      // Regelbehandlungszeit of its position, and dragging it would change the
      // calendar without changing the bill.
      //
      // The exception is a position priced PER Zeitintervall (§ 125a
      // Ergotherapie), where the therapist chooses the Therapiezeit per
      // Behandlungstermin and the length of the slot IS the billed quantity.
      // For those, and only those, resizing is how that choice is expressed.
      // A locked appointment stays locked either way.
      durationEditable: !isLocked && isIntervalPriced(timeSlot.therapy),
      // Grouped participants are drawn as chips inside the group event; ungrouped
      // ones must be drawn as standalone events or they vanish from the calendar.
      // A slot with no therapy resolves to 'none' in heatTreatmentBackground —
      // fine for the shapes that legitimately have none, but a CANCELLED slot
      // must never be hidden by a data gap: the whole point of the
      // "Stornierungen" toggle is to show it (a missing backend therapy join
      // silently blanked the entire view this way once already).
      display: isGroupTimeSlotPatient ? 'none' : (options.isBackgroundEvent ? "background" : groupTimeSlotTherapist ? "block" : (appointment.isCancelled && !timeSlot.therapy) ? "block" : heatTreatmentBackground(timeSlot.therapy!)),
      isHeatTreatment: timeSlot.therapy?.isHeatTreatment,

      // Visual styling
      color:mobiliTheme.lowLevelContainerBackground,
      classNames: [
        appointment.isCancelled ? 'cancelled' : '',
        appointment.hasNotes ? 'has-notes' : '',
        isLocked ? 'locked' : '',
        (timeSlot.attended || allParticipantsAttended) ? 'attended-event' : ''
      ].filter(Boolean),

      // All custom properties go in extendedProps
      extendedProps: {
        eventType: 'appointment' as const,
        appointmentId: appointment.id,
        timeSlotId: timeSlot.id,
        groupAppointmentId: timeSlot?.groupAppointmentId,
        participants: appointment.participants || [],
        multipleTimeSlots: appointment.hasMultipleTimeSlots,
        containsHeatTreatment: appointment.timeSlots.some(ts => ts.therapy?.isHeatTreatment),
        hasNotes: appointment.hasNotes,
        isCancelled: appointment.isCancelled,
        attended: timeSlot.attended,
        informing: timeSlot.informing,
        
        // Extended properties for tooltips/context
        patient: timeSlot.patient || appointment.patient,
        therapy: timeSlot.therapy,
        therapyRxId: timeSlot.therapyRxId,
        user: timeSlot.user,
        address: appointment.address,

        rx: timeSlot.rx,
        visitType: timeSlot.visitType,
        visitFee: timeSlot.visitFee,
        
        timeSlotType: timeSlot.type,

        reason: appointment.reason,
        treatmentIndex: timeSlot.treatmentIndex,
        wasDocumentedCount: timeSlot?.wasDocumentedCount,
        sublocality: sublocality,
        frequency: appointment.frequency,
        isContinuous: false,
      }
    };
  });
};

export const adaptContinuousAppointmentToCalendarEvent = (
  continuousAppointment: ClientContinuousAppointment,
  options: AppointmentTransformOptions = {}
): CalendarEvent[] => {
  const { groupCheck = false, lockedAppointments = [], sublocalities } = options;
  // Same rule as concrete appointments: back-to-back slots on one therapist
  // move together, everything else moves alone. The ungrouped branch used to
  // fall back to the appointment id, which is still a shared groupId — so
  // "nicht gruppieren" left Dauertermin slots glued together anyway.
  const runIds = buildContiguousRunIds(
    continuousAppointment.id,
    continuousAppointment.timeSlots,
    groupCheck
  );

  return continuousAppointment.timeSlots.map(timeSlot => {
    const groupTimeSlotTherapist = timeSlot.type === "ContinuousGroupTimeSlotTherapist";
    const isGroupTimeSlotPatient = timeSlot.type === "ContinuousGroupTimeSlotPatient";
    const groupId = isGroupTimeSlotPatient
      ? `${timeSlot.groupAppointmentId}-${timeSlot.user?.id}`
      : (runIds.get(timeSlot.id) ?? "");
    
    const isLocked = lockedAppointments.includes(continuousAppointment.id);
    const sublocality = sublocalities?.find(subloc => subloc?.addressId === continuousAppointment.address?.id);

    const allParticipantsAttended = groupTimeSlotTherapist
      ? (continuousAppointment.participants?.length
          ? continuousAppointment.participants.every(participant =>
              participant.timeSlots?.some(ts => ts.attended)
            )
          : false)
      : false;

    if (!timeSlot.therapy) {
      // this can happen for ContinuousGroupTimeSlotTherapist
      console.warn("Therapy is undefined for continuous time slot.", timeSlot);
    }

    return {
      id: timeSlot.id,
      title: continuousAppointment.displayTitle,
      start: timeSlot.start.toISOString(),
      end: timeSlot.end.toISOString(),
      resourceId: timeSlot.user?.id,
      // Same rule as the concrete calendar (MD-328): only an interval-priced
      // position may have its duration changed, because for those the length
      // is the billed quantity rather than a fixed Regelbehandlungszeit.
      //
      // It applies to a Dauertermin too. The recurring slot is what generates
      // the concrete Behandlungstermine, so its length is the Therapiezeit the
      // series will be planned at — a therapist setting up a weekly 60-minute
      // Ergo Blanko series has to be able to say 60 here, not schedule 30 and
      // stretch every instance afterwards.
      //
      // The save side already agrees: the continuous path builds its slots
      // through the same `eventToTimeSlotDto`, so a resize here is trimmed to
      // whole Zeitintervalle exactly as it is on a concrete appointment.
      durationEditable: isIntervalPriced(timeSlot.therapy),
      groupId,

      // startTime, endTime and daysOfWeek are used for recurring events in FullCalendar
      // They are essential for proper rendering of continuous appointments
      // even though we provide explicit start and end times above
      startTime: moment(timeSlot.start!).format("HH:mm:ss"),
      endTime: moment(timeSlot.end!).format("HH:mm:ss"),
      daysOfWeek: [moment(timeSlot.start!).day().toString()],

      editable: !isLocked,
      resizable: false,
      display: isGroupTimeSlotPatient ? 'none' : (groupTimeSlotTherapist ? "block" : heatTreatmentBackground(timeSlot.therapy!)),
      isHeatTreatment: timeSlot.therapy?.isHeatTreatment,
      
      // Visual styling
      color: mobiliTheme.lowLevelContainerBackground,
      classNames: [
        continuousAppointment.isCancelled ? 'cancelled' : '',
        continuousAppointment.hasNotes ? 'has-notes' : '',
        isLocked ? 'locked' : '',
        (timeSlot.attended || allParticipantsAttended) ? 'attended-event' : ''
      ].filter(Boolean),
      
      // All custom properties go in extendedProps
      extendedProps: {
        eventType: 'appointment' as const,
        appointmentId: continuousAppointment.id,
        timeSlotId: timeSlot.id,
        groupAppointmentId: timeSlot?.groupAppointmentId,
        participants: continuousAppointment?.participants || [],
        
        multipleTimeSlots: continuousAppointment.hasMultipleTimeSlots,
        containsHeatTreatment: continuousAppointment.containsHeatTreatment,
        hasNotes: continuousAppointment.hasNotes,
        isCancelled: continuousAppointment.isCancelled,
        attended: timeSlot.attended,
        informing: timeSlot.informing,
        
        // Extended properties for tooltips/context
        patient: continuousAppointment.patient,
        therapy: timeSlot.therapy,
        therapyRxId: timeSlot.therapyRxId,
        user: timeSlot.user,
        address: continuousAppointment.address,

        rx: timeSlot.rx,
        visitType: timeSlot.visitType,
        visitFee: timeSlot.visitFee,
        
        timeSlotType: timeSlot.type,

        reason: timeSlot.reason,
        treatmentIndex: timeSlot.treatmentIndex,
        sublocality: sublocality,
        frequency: continuousAppointment.frequency,
        isContinuous: true,
      }
    };
  });
};

export const heatTreatmentBackground = (therapy: ClientTherapy | undefined): string => {
  enum display {
    block = "block",
    background = "background",
    none = "none",
  }

  if (!therapy) {
    return display.none;
  }
  // On a ClientTherapy the passive flag is `isHeatTreatment` — computed from
  // Therapy.isPassive at the api-to-client boundary (adaptTherapyFromApi).
  // `isPassive` does not exist on this shape; testing it here returned "block"
  // for every slot and stripped passive applications of their .fc-bg-event
  // (bordeaux background) styling.
  if (therapy.isHeatTreatment) {
    return display.background;
  } else {
    return display.block;
  }
};

 
